export const parseGstPct = (value: unknown): number => {
  const n = Number(String(value ?? "").replace(/%/g, "").trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const lineQty = (line: { qty?: unknown }): number => {
  const n = Number(line?.qty);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return n;
};

export const moneyAmount = (value: unknown): number | null => {
  if (value == null || String(value).trim() === "") return null;
  const amount = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(amount) ? amount : null;
};

/** A typed rate wins. A blank or zero rate falls back to the product sale price. */
export const defaultRate = (typed: unknown, saved: unknown, sellPrice: unknown): number => {
  const typedAmount = moneyAmount(typed);
  if (typedAmount != null && typedAmount !== 0) return typedAmount;
  const savedAmount = moneyAmount(saved);
  if (savedAmount != null && savedAmount !== 0) return savedAmount;
  return moneyAmount(sellPrice) ?? typedAmount ?? 0;
};

export const lineRate = (line: { billedPrice?: unknown; proposedPrice?: unknown }): number => {
  const n = Number(line?.billedPrice ?? line?.proposedPrice ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const lineDiscountPct = (line: { discount?: unknown }): number => {
  const n = Number(line?.discount ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 100);
};

export const lineAmount = (line: {
  qty?: unknown;
  billedPrice?: unknown;
  proposedPrice?: unknown;
  discount?: unknown;
}): number => {
  const gross = lineQty(line) * lineRate(line);
  return gross * (1 - lineDiscountPct(line) / 100);
};

export const lineGstAmt = (line: {
  qty?: unknown;
  billedPrice?: unknown;
  proposedPrice?: unknown;
  gstRate?: unknown;
}): number => (lineAmount(line) * parseGstPct(line.gstRate)) / 100;

export const standingMemoLines = (lines: Array<Record<string, unknown>> = []) =>
  lines.filter((line) => ["ACTIVE", "BILLED", "RETURNED", "KEPT"].includes(String(line.lineStatus || "")));

/** Lines whose value the memo stands for: an approval memo counts only pieces still out with the buyer. */
export const memoValueLines = (memo: { type?: unknown; lines?: unknown }) => {
  const lines = standingMemoLines(Array.isArray(memo?.lines) ? memo.lines as Array<Record<string, unknown>> : []);
  return memo?.type === "APPROVAL" ? lines.filter((line) => line.lineStatus === "ACTIVE") : lines;
};

export const memoAudit = (memo: { audit?: unknown; lines?: unknown; billedAt?: unknown; memoCode?: unknown }) => {
  const stored = Array.isArray(memo?.audit) ? memo.audit : [];
  if (stored.length) {
    return stored as Array<Record<string, unknown>>;
  }
  const lines = Array.isArray(memo?.lines) ? memo.lines as Array<Record<string, unknown>> : [];
  const voided = lines.filter((line) => line.lineStatus === "VOIDED");
  if (!voided.length) {
    return [];
  }
  const reversed = lines.filter((line) => line.lineStatus === "REVERSED");
  return [{
    at: (() => {
      const times = voided.map((line) => line.voidedAt).filter(Boolean).sort();
      return times[times.length - 1] || memo?.billedAt || null;
    })(),
    reason: reversed.map((line) => line.remarks).filter(Boolean).join("; "),
    actorName: reversed.find((line) => line.actorName)?.actorName || "",
    memoCode: memo?.memoCode,
    lines: lines
      .filter((line) => line.lineStatus !== "REVERSED" && line.lineStatus !== "DROPPED")
      .map((line) => ({ ...line, lineStatus: line.lineStatus === "VOIDED" ? "BILLED" : line.lineStatus })),
  }];
};

export const memoTotals = (lines: Array<Record<string, unknown>>) => {
  const subTotal = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const gst = lines.reduce((sum, line) => sum + lineGstAmt(line), 0);
  return { subTotal, gst, total: subTotal + gst, hasGst: gst > 0 };
};

export const formatInr = (value: number): string =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Every invoice a credit note covers (one per source memo), falling back to the header for old notes. */
export const creditSourceInvoices = (memo: { invoiceNumber?: unknown; lines?: unknown }): string[] => {
  const lines = Array.isArray(memo?.lines) ? memo.lines as Array<Record<string, unknown>> : [];
  const numbers = standingMemoLines(lines).map((line) => String(line.sourceInvoiceNumber || "")).filter(Boolean);
  const unique = Array.from(new Set(numbers));
  return unique.length ? unique : memo?.invoiceNumber ? [String(memo.invoiceNumber)] : [];
};
